"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

export type RecaptchaFieldRef = {
  /**
   * Run an invisible reCAPTCHA Enterprise check and resolve a fresh token.
   * Call this at submit time (Enterprise tokens are single-use, ~2 min TTL).
   */
  execute: (action?: string) => Promise<string | null>;
  /** Back-compat no-op (invisible reCAPTCHA has no widget to reset). */
  reset: () => void;
};

type RecaptchaFieldProps = {
  /** Optional: receives the token after execute() (or null on reset/failure). */
  onVerify?: (token: string | null) => void;
  /** Default action label reported to the Enterprise assessment. */
  action?: string;
};

const SCRIPT_ID = "recaptcha-enterprise-script";

/** Load enterprise.js once and resolve when grecaptcha.enterprise is available. */
function loadEnterpriseScript(siteKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return resolve();
    const w = window as unknown as { grecaptcha?: { enterprise?: unknown } };
    if (w.grecaptcha?.enterprise) return resolve();

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load reCAPTCHA Enterprise"))
      );
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(
      siteKey
    )}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load reCAPTCHA Enterprise"));
    document.head.appendChild(script);
  });
}

const RecaptchaField = forwardRef<RecaptchaFieldRef, RecaptchaFieldProps>(
  function RecaptchaField({ onVerify, action = "contact" }, ref) {
    const siteKey = (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "").trim();
    const startedRef = useRef(false);

    // Pre-load the script so the first submit is fast.
    useEffect(() => {
      if (!siteKey || startedRef.current) return;
      startedRef.current = true;
      loadEnterpriseScript(siteKey).catch((e) =>
        console.error("reCAPTCHA Enterprise failed to load:", e)
      );
    }, [siteKey]);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => onVerify?.(null),
        execute: async (a?: string) => {
          if (!siteKey) return null;
          try {
            await loadEnterpriseScript(siteKey);
            const w = window as unknown as {
              grecaptcha: {
                enterprise: {
                  ready: (cb: () => void) => void;
                  execute: (key: string, opts: { action: string }) => Promise<string>;
                };
              };
            };
            const token = await new Promise<string>((resolve, reject) => {
              w.grecaptcha.enterprise.ready(() => {
                w.grecaptcha.enterprise
                  .execute(siteKey, { action: a ?? action })
                  .then(resolve)
                  .catch(reject);
              });
            });
            onVerify?.(token);
            return token;
          } catch (err) {
            console.error("reCAPTCHA Enterprise execute failed:", err);
            onVerify?.(null);
            return null;
          }
        },
      }),
      [siteKey, action, onVerify]
    );

    if (!siteKey) {
      return (
        <p className="text-sm text-red-600 text-center">
          reCAPTCHA is not configured. Set NEXT_PUBLIC_RECAPTCHA_SITE_KEY.
        </p>
      );
    }

    // Invisible/score-based flow: no checkbox. Show the required attribution notice.
    return (
      <p className="text-xs text-gray-500 text-center">
        This site is protected by reCAPTCHA Enterprise and the Google{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Privacy Policy
        </a>{" "}
        and{" "}
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Terms of Service
        </a>{" "}
        apply.
      </p>
    );
  }
);

export default RecaptchaField;
